import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);

  const save = async () => {
    setSaving(true);
    const payload = {
      deposit_enabled: s.deposit_enabled,
      default_deposit_amount: Number(s.default_deposit_amount) || 0,
      razorpay_key_id: s.razorpay_key_id || "",
      contact_phone: s.contact_phone || "",
      contact_whatsapp: s.contact_whatsapp || "",
      contact_email: s.contact_email || "",
      contact_address: s.contact_address || "",
      google_maps_embed: s.google_maps_embed || "",
      panchang_enabled: !!s.panchang_enabled,
      panchang_banner_enabled: !!s.panchang_banner_enabled,
      panchang_card_enabled: !!s.panchang_card_enabled,
      panchang_override_text: s.panchang_override_text || "",
    };
    if (secret) payload.razorpay_key_secret = secret;
    const { data } = await api.patch("/settings", payload);
    setS(data); setSecret(""); setSaving(false);
    toast.success("Settings saved");
  };

  if (!s) return <div className="opacity-65">Loading...</div>;

  return (
    <div data-testid="admin-settings-page">
      <h1 className="font-heading text-3xl">Settings</h1>

      <section className="mt-6 bg-white border border-[color:var(--border)] rounded-md p-5">
        <h2 className="font-heading text-2xl">Booking Deposit</h2>
        <div className="mt-4 flex items-center gap-3">
          <Switch checked={!!s.deposit_enabled} onCheckedChange={(v) => setS({ ...s, deposit_enabled: v })} data-testid="deposit-toggle" />
          <span className="text-sm">{s.deposit_enabled ? "Deposit Required" : "Deposit Optional (OFF)"}</span>
        </div>
        <div className="mt-4">
          <div className="text-xs opacity-60 mb-1">Default Deposit Amount (INR)</div>
          <Input type="number" value={s.default_deposit_amount} onChange={(e) => setS({ ...s, default_deposit_amount: e.target.value })} className="w-40" data-testid="default-deposit" />
          <p className="text-xs opacity-60 mt-2">"Booking amount will be adjusted against final pooja charges."</p>
        </div>
      </section>

      <section className="mt-6 bg-white border border-[color:var(--border)] rounded-md p-5">
        <h2 className="font-heading text-2xl">Panchang Feature</h2>
        <p className="text-sm opacity-65 mt-1">Daily Tithi, Nakshatra and Vara are computed automatically using the Lahiri ayanamsa (Asia/Kolkata). No manual maintenance required.</p>
        <div className="mt-5 space-y-4">
          <div className="flex items-center gap-3">
            <Switch checked={!!s.panchang_enabled} onCheckedChange={(v) => setS({ ...s, panchang_enabled: v })} data-testid="panchang-master-toggle" />
            <span className="text-sm">Panchang Feature {s.panchang_enabled ? "Enabled" : "Disabled"}</span>
          </div>
          <div className={`flex items-center gap-3 ${!s.panchang_enabled ? "opacity-50 pointer-events-none" : ""}`}>
            <Switch checked={!!s.panchang_banner_enabled} onCheckedChange={(v) => setS({ ...s, panchang_banner_enabled: v })} data-testid="panchang-banner-toggle" />
            <span className="text-sm">Hero Panchang Banner</span>
          </div>
          <div className={`flex items-center gap-3 ${!s.panchang_enabled ? "opacity-50 pointer-events-none" : ""}`}>
            <Switch checked={!!s.panchang_card_enabled} onCheckedChange={(v) => setS({ ...s, panchang_card_enabled: v })} data-testid="panchang-card-toggle" />
            <span className="text-sm">Homepage Panchang Card</span>
          </div>
          <div className={!s.panchang_enabled ? "opacity-50 pointer-events-none" : ""}>
            <div className="text-xs opacity-65 mb-1">Custom Override Text (optional)</div>
            <Input
              placeholder="e.g., श्रावण मास विशेष पूजा आरंभ"
              value={s.panchang_override_text || ""}
              onChange={(e) => setS({ ...s, panchang_override_text: e.target.value })}
              data-testid="panchang-override-input"
            />
            <p className="text-[11px] opacity-60 mt-2">When non-empty, this replaces the auto-calculated Panchang line in the hero banner.</p>
          </div>
        </div>
      </section>


      <section className="mt-6 bg-white border border-[color:var(--border)] rounded-md p-5">
        <h2 className="font-heading text-2xl">Razorpay</h2>
        <p className="text-sm opacity-65 mt-1">Add your Razorpay credentials to enable online deposits. Get them from <a className="underline" href="https://dashboard.razorpay.com" target="_blank" rel="noreferrer">dashboard.razorpay.com</a>.</p>
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          <div>
            <div className="text-xs opacity-60 mb-1">Key ID (public)</div>
            <Input value={s.razorpay_key_id || ""} onChange={(e) => setS({ ...s, razorpay_key_id: e.target.value })} placeholder="rzp_live_..." data-testid="razorpay-key-id" />
          </div>
          <div>
            <div className="text-xs opacity-60 mb-1">Key Secret {s.razorpay_key_secret_set && <span className="text-green-700">(saved)</span>}</div>
            <Input value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={s.razorpay_key_secret_set ? "Leave blank to keep existing" : "Enter secret"} type="password" data-testid="razorpay-key-secret" />
          </div>
        </div>
      </section>

      <section className="mt-6 bg-white border border-[color:var(--border)] rounded-md p-5">
        <h2 className="font-heading text-2xl">Contact Information</h2>
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          <Input placeholder="Phone" value={s.contact_phone || ""} onChange={(e) => setS({ ...s, contact_phone: e.target.value })} data-testid="contact-phone-input" />
          <Input placeholder="WhatsApp" value={s.contact_whatsapp || ""} onChange={(e) => setS({ ...s, contact_whatsapp: e.target.value })} data-testid="contact-whatsapp-input" />
          <Input placeholder="Email" value={s.contact_email || ""} onChange={(e) => setS({ ...s, contact_email: e.target.value })} data-testid="contact-email-input-admin" />
          <Input placeholder="Maps Embed URL" value={s.google_maps_embed || ""} onChange={(e) => setS({ ...s, google_maps_embed: e.target.value })} data-testid="contact-maps-input" />
          <div className="md:col-span-2">
            <Textarea placeholder="Address" value={s.contact_address || ""} onChange={(e) => setS({ ...s, contact_address: e.target.value })} data-testid="contact-address-input" />
          </div>
        </div>
      </section>

      <div className="mt-6 flex justify-end">
        <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-full btn-brand text-sm disabled:opacity-60" data-testid="settings-save-btn">
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
